import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../../components/Dashboard/DashboardLayout';
import ExamTimer from '../../../components/Exam/ExamTimer';
import { Button } from '../../../components/ui/button';
import { examB2API } from '../../../services/api';
import { SPRECHEN_TASKS as FALLBACK_TASKS, REDEMITTEL as FALLBACK_REDEMITTEL } from '../../../data/exam/b2';
import {
  ArrowLeftIcon,
  MicrophoneIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import VoicePracticeRecorder from '../../../components/Exam/VoicePracticeRecorder';

const SprechenPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [redemittel, setRedemittel] = useState(FALLBACK_REDEMITTEL);
  const [loading, setLoading] = useState(true);
  const [activeTaskIdx, setActiveTaskIdx] = useState(0);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [phase, setPhase] = useState('select');
  const [notes, setNotes] = useState('');
  const [checkedCriteria, setCheckedCriteria] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    examB2API.getTasks('sprechen')
      .then(({ tasks: t, redemittel: r }) => {
        setTasks(t);
        if (r) setRedemittel(r);
      })
      .catch(() => setTasks(FALLBACK_TASKS))
      .finally(() => setLoading(false));
  }, []);

  const task = tasks[activeTaskIdx];

  const toggleCriterion = (key) => {
    setCheckedCriteria((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const resetTask = () => {
    setSelectedTopic(null);
    setSelectedScenario(null);
    setPhase('select');
    setNotes('');
    setCheckedCriteria({});
    setLastResult(null);
  };

  const handleSubmit = async () => {
    if (!task) return;
    setSubmitting(true);
    const criteria = task.evaluationCriteria || [];
    const selfCheck = criteria.map((_, i) => !!checkedCriteria[i]);

    try {
      const res = await examB2API.submitSprechen({
        taskId: task.id,
        topicId: selectedTopic?.id,
        scenarioId: selectedScenario?.id,
        notes,
        selfCheck,
      });
      setLastResult(res.data.data);
      setPhase('done');
    } catch (err) {
      console.error(err);
      setPhase('done');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!task) return null;

  const criteriaCount = Object.values(checkedCriteria).filter(Boolean).length;
  const totalCriteria = task.evaluationCriteria?.length || 0;

  return (
    <DashboardLayout>
      <div className="page-shell-wide font-body">
        <button
          onClick={() => navigate('/exam/b2')}
          className="mb-6 flex items-center gap-2 text-xs font-bold text-brand-text/50 hover:text-brand-text"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Kthehu
        </button>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <span className="text-xs font-black uppercase tracking-widest text-orange-600">Sprechen B2</span>
            <h1 className="text-2xl font-black text-brand-text">{task.title}</h1>
          </div>
          {phase === 'prep' && <ExamTimer totalMinutes={task.prepMinutes || 15} label="Përgatitje" />}
          {phase === 'speak' && <ExamTimer totalMinutes={task.speakMinutes} label="Folësimi" />}
        </div>

        <div className="flex gap-2 mb-6">
          {tasks.map((t, i) => (
            <button
              key={t.id}
              onClick={() => { setActiveTaskIdx(i); resetTask(); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                i === activeTaskIdx ? 'bg-orange-600 text-white' : 'bg-gray-100 text-brand-text/60 hover:bg-brand-primary/10'
              }`}
            >
              Teil {t.teil}
            </button>
          ))}
        </div>

        <p className="text-sm text-brand-text/60 mb-6 italic">{task.instruction}</p>

        {task.topics && phase === 'select' && (
          <div className="space-y-4">
            {task.topics.map((topic) => (
              <div
                key={topic.id}
                onClick={() => { setSelectedTopic(topic); setPhase('prep'); }}
                className="bg-white rounded-2xl border-2 border-brand-secondary/15 hover:border-orange-400 p-6 cursor-pointer transition-all hover:shadow-md"
              >
                <h3 className="font-bold text-brand-text mb-2">{topic.title}</h3>
                <p className="text-sm text-brand-text/70">{topic.prompt}</p>
              </div>
            ))}
          </div>
        )}

        {task.scenarios && phase === 'select' && (
          <div className="space-y-4">
            {task.scenarios.map((scenario) => (
              <div
                key={scenario.id}
                onClick={() => { setSelectedScenario(scenario); setPhase('prep'); }}
                className="bg-white rounded-2xl border-2 border-brand-secondary/15 hover:border-orange-400 p-6 cursor-pointer transition-all hover:shadow-md"
              >
                <h3 className="font-bold text-brand-text mb-2">{scenario.title}</h3>
                <p className="text-sm text-brand-text/70">{scenario.situation}</p>
              </div>
            ))}
          </div>
        )}

        {phase === 'prep' && (
          <div>
            {selectedTopic && (
              <div className="bg-orange-50 rounded-2xl p-6 mb-6 border border-orange-100">
                <h3 className="font-bold text-brand-text mb-2">{selectedTopic.title}</h3>
                <p className="text-sm text-brand-text/80 mb-4">{selectedTopic.prompt}</p>
                <ul className="text-sm text-brand-text/70 space-y-1">
                  {selectedTopic.bulletPoints?.map((bp, i) => <li key={i}>• {bp}</li>)}
                </ul>
              </div>
            )}

            {selectedScenario && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 rounded-2xl p-5 border border-blue-100">
                  <p className="text-xs font-black uppercase text-blue-700 mb-2">{selectedScenario.roleA?.label}</p>
                  <ul className="text-sm text-brand-text/80 space-y-1">
                    {selectedScenario.roleA?.arguments?.map((a, i) => <li key={i}>• {a}</li>)}
                  </ul>
                </div>
                <div className="bg-red-50 rounded-2xl p-5 border border-red-100">
                  <p className="text-xs font-black uppercase text-red-700 mb-2">{selectedScenario.roleB?.label}</p>
                  <ul className="text-sm text-brand-text/80 space-y-1">
                    {selectedScenario.roleB?.arguments?.map((a, i) => <li key={i}>• {a}</li>)}
                  </ul>
                </div>
              </div>
            )}

            <div className="mb-6">
              <p className="text-xs font-black uppercase tracking-widest text-brand-text/60 mb-2">Redemittel</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(redemittel).map(([cat, phrases]) => (
                  <div key={cat} className="bg-gray-50 rounded-xl p-3">
                    <p className="text-[10px] font-black uppercase text-brand-text/50 mb-1">{cat}</p>
                    {phrases.slice(0, 2).map((p, i) => (
                      <p key={i} className="text-xs text-brand-text/70">{p}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Shënimet tuaja për përgatitjen..."
              className="w-full h-32 p-4 rounded-xl border-2 border-brand-secondary/20 focus:border-orange-400 focus:outline-none text-sm mb-4"
            />

            <button
              onClick={() => setPhase('speak')}
              className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-700"
            >
              <MicrophoneIcon className="w-5 h-5" /> Fillo folësimin
            </button>
          </div>
        )}

        {phase === 'speak' && (
          <div className="space-y-6">
            <VoicePracticeRecorder
              targetMinutes={task.speakMinutes || 2}
              onTranscriptChange={(text) => {
                // optionally append or save transcription
              }}
            />

            <div className="space-y-2 mb-6">
              {task.evaluationCriteria?.map((c, i) => (
                <label key={i} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 cursor-pointer hover:bg-brand-primary/10">
                  <input
                    type="checkbox"
                    checked={!!checkedCriteria[i]}
                    onChange={() => toggleCriterion(i)}
                    className="mt-0.5 accent-orange-600"
                  />
                  <span className="text-sm text-brand-text/80">{c}</span>
                </label>
              ))}
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="gap-2"
            >
              <CheckCircleIcon className="w-5 h-5" />
              {submitting ? 'Duke ruajtur...' : 'Përfundo & ruaj'}
            </Button>
          </div>
        )}

        {phase === 'done' && (
          <div className="text-center">
            <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-black text-brand-text mb-2">Teil {task.teil} — Ruajtur!</h2>
            <p className="text-brand-text/60 mb-2">
              Self-check: {lastResult?.checked_criteria ?? criteriaCount}/{lastResult?.total_criteria ?? totalCriteria}
            </p>
            {lastResult?.score != null && (
              <p className={`text-2xl font-black mb-6 ${lastResult.passed ? 'text-green-600' : 'text-orange-500'}`}>
                {lastResult.score}%
              </p>
            )}
            <div className="flex gap-3 justify-center">
              <Button onClick={resetTask}>
                Provo përsëri
              </Button>
              {activeTaskIdx < tasks.length - 1 ? (
                <button
                  onClick={() => { setActiveTaskIdx(1); resetTask(); }}
                  className="px-6 py-3 bg-orange-600 text-white rounded-xl text-sm font-bold"
                >
                  Teil 2 →
                </button>
              ) : (
                <Button onClick={() => navigate('/exam/b2')}>
                  Kthehu te modulet
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SprechenPage;
